import React, { useState, useEffect } from 'react';
import { db, auth, functions } from './firebase';
import {
  collection, doc, getDoc, getDocs, setDoc, serverTimestamp, query, where
} from 'firebase/firestore';
import { onAuthStateChanged, createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import {
  BookOpen, ArrowLeft, ShoppingCart, Headphones, BookText, Package,
  ExternalLink, Lock, Loader2, Mail, RefreshCw, User, Tag, X
} from 'lucide-react';

const SIGNED_TYPES = ['Paperback Signed', 'Hardback Signed', 'Paperback Special Edition Box', 'Hardback Special Edition Box'];

export default function AuthorStore({ authorId }) {
  const [author, setAuthor] = useState(null);
  const [branding, setBranding] = useState(null);
  const [books, setBooks] = useState([]);
  const [guestSettings, setGuestSettings] = useState({ ebooks: true, audiobooks: true, physical: true });
  const [loading, setLoading] = useState(true);

  // Products per book: { [bookId]: { ebooks: [], audiobooks: [], products: [] } }
  const [bookProducts, setBookProducts] = useState({});

  // Auth + checkout state
  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [showCheckout, setShowCheckout] = useState(null); // { type, item, bookTitle, isPreOrder }
  const [checkoutForm, setCheckoutForm] = useState({
    name: '', email: '', password: '',
    shippingAddress: { line1: '', line2: '', city: '', postalCode: '', country: '' },
  });
  const [checkoutStep, setCheckoutStep] = useState('form'); // form | delivery | verify | processing
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  // Delivery preference for ebook pre-orders
  const [deliveryPref, setDeliveryPref] = useState('online'); // online | device
  const [deviceEmail, setDeviceEmail] = useState('');
  // Promo code
  const [storePromoCode, setStorePromoCode] = useState('');
  const [storePromoApplied, setStorePromoApplied] = useState(null);
  const [storePromoError, setStorePromoError] = useState('');
  const [storePromoValidating, setStorePromoValidating] = useState(false);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setCurrentUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  // Load author data
  useEffect(() => {
    async function load() {
      try {
        // Author profile
        const authorSnap = await getDoc(doc(db, 'authors', authorId));
        if (!authorSnap.exists()) { setLoading(false); return; }
        setAuthor({ id: authorSnap.id, ...authorSnap.data() });

        // Branding
        try {
          const brandingSnap = await getDoc(doc(db, 'authors', authorId, 'settings', 'branding'));
          if (brandingSnap.exists()) setBranding(brandingSnap.data());
        } catch { /* skip */ }

        // Guest access settings
        try {
          const gaSnap = await getDoc(doc(db, 'authors', authorId, 'settings', 'guestAccess'));
          if (gaSnap.exists()) setGuestSettings(prev => ({ ...prev, ...gaSnap.data() }));
        } catch { /* skip */ }

        // Books
        const booksSnap = await getDocs(collection(db, 'authors', authorId, 'books'));
        const bookList = [];
        const productsMap = {};

        // Load guest-visible ebooks and audiobooks (filtered queries to satisfy Firestore rules)
        let allEbooks = [];
        let allAudiobooks = [];
        try {
          const ebQ = query(collection(db, 'authors', authorId, 'ebooks'), where('visibleToGuests', '==', true));
          const ebSnap = await getDocs(ebQ);
          ebSnap.forEach(d => allEbooks.push({ id: d.id, ...d.data(), authorId }));
        } catch (err) { console.warn('Failed to load ebooks:', err.message); }
        try {
          const abQ = query(collection(db, 'authors', authorId, 'audiobooks'), where('visibleToGuests', '==', true));
          const abSnap = await getDocs(abQ);
          abSnap.forEach(d => allAudiobooks.push({ id: d.id, ...d.data(), authorId }));
        } catch (err) { console.warn('Failed to load audiobooks:', err.message); }

        // Show all books (no status filtering)
        for (const bookDoc of booksSnap.docs) {
          const bd = bookDoc.data();
          bookList.push({ id: bookDoc.id, ...bd });

          const ebooks = allEbooks.filter(e => e.bookId === bookDoc.id);
          const audiobooks = allAudiobooks.filter(a => a.bookId === bookDoc.id);

          // Physical products — use filtered query to satisfy rules
          const products = [];
          try {
            const prodQ = query(
              collection(db, 'authors', authorId, 'books', bookDoc.id, 'products'),
              where('available', '==', true)
            );
            const prodSnap = await getDocs(prodQ);
            prodSnap.forEach(d => products.push({ id: d.id, ...d.data() }));
          } catch (err) { console.warn('Failed to load products for', bookDoc.id, err.message); }

          productsMap[bookDoc.id] = { ebooks, audiobooks, products };
        }

        setBooks(bookList);
        setBookProducts(productsMap);
      } catch (err) {
        console.error('Failed to load store:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [authorId]);

  function getBookStatusLabel(book) {
    if (book.status === 'launched') return { label: 'Live', className: 'bg-green-100 text-green-700' };
    if (book.status === 'pre_launch_active') return { label: 'Coming Soon', className: 'bg-blue-100 text-blue-700' };
    if (book.status === 'upcoming') return { label: 'Upcoming', className: 'bg-slate-100 text-slate-600' };
    return { label: book.status || 'Available', className: 'bg-slate-100 text-slate-600' };
  }

  function isGuestVisible(item, type) {
    // visibleToGuests must be true (or undefined defaults to true)
    return item.visibleToGuests !== false;
  }

  function isGuestPurchasable(item, type) {
    // availableToGuests must be explicitly true
    if (item.availableToGuests === true) return true;
    // Fallback to old guestAccess field
    if (item.availableToGuests === undefined && item.guestAccess === true) return true;
    return false;
  }

  // Guest checkout: create account, verify email, then redirect to Stripe
  async function handleGuestCheckout(e) {
    e.preventDefault();
    setCheckoutError('');
    setCheckoutLoading(true);

    try {
      if (!currentUser) {
        // Create guest account
        if (!checkoutForm.name || !checkoutForm.email || !checkoutForm.password) {
          throw new Error('Please fill in all required fields.');
        }
        if (checkoutForm.password.length < 6) {
          throw new Error('Password must be at least 6 characters.');
        }
        const cred = await createUserWithEmailAndPassword(auth, checkoutForm.email, checkoutForm.password);
        await sendEmailVerification(cred.user);

        // Create guest doc
        await setDoc(doc(db, 'guests', cred.user.uid), {
          name: checkoutForm.name.trim(),
          email: checkoutForm.email.toLowerCase(),
          emailVerified: false,
          shippingAddress: checkoutForm.shippingAddress,
          createdAt: serverTimestamp(),
          authorId,
          upgradedToPortal: false,
        });

        // Set guest role
        httpsCallable(functions, 'setCustomClaims')({ uid: cred.user.uid, role: 'guest' }).catch(() => {});
        setCheckoutStep('verify');
      } else {
        // Already logged in — go straight to payment
        await proceedToStripe();
      }
    } catch (err) {
      setCheckoutError(err.message);
    } finally {
      setCheckoutLoading(false);
    }
  }

  async function handleVerifyAndPay() {
    if (!currentUser) return;
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      await currentUser.reload();
      if (!currentUser.emailVerified) {
        setCheckoutError('Please verify your email first. Check your inbox.');
        setCheckoutLoading(false);
        return;
      }
      // Update guest doc
      try {
        await setDoc(doc(db, 'guests', currentUser.uid), { emailVerified: true }, { merge: true });
      } catch { /* skip */ }
      await proceedToStripe();
    } catch (err) {
      setCheckoutError(err.message);
      setCheckoutLoading(false);
    }
  }

  async function validateStorePromo() {
    if (!storePromoCode.trim() || !showCheckout) return;
    setStorePromoValidating(true);
    setStorePromoError('');
    try {
      const validate = httpsCallable(functions, 'validatePromoCode');
      const result = await validate({
        authorId: showCheckout.item?.authorId || authorId,
        code: storePromoCode.trim(),
        productType: showCheckout.type || 'physical',
      });
      const data = result.data;
      if (data.valid) {
        setStorePromoApplied({
          id: data.promoCodeId,
          code: data.code,
          discountType: data.discountType,
          discountValue: data.discountValue,
          freeShipping: !!data.freeShipping,
        });
        setStorePromoError('');
      } else {
        setStorePromoError(data.reason || 'Invalid or expired promo code');
        setStorePromoApplied(null);
      }
    } catch (err) {
      setStorePromoError(err.message || 'Could not validate code');
    } finally {
      setStorePromoValidating(false);
    }
  }

  async function proceedToStripe() {
    if (!showCheckout) return;
    const { type, item, isPreOrder } = showCheckout;
    setCheckoutStep('processing');
    setCheckoutLoading(true);

    try {
      if (isPreOrder) {
        // Pre-orders go through createPreOrder (no Stripe for now)
        const guestName = checkoutForm.name || currentUser?.displayName || '';
        const guestEmail = checkoutForm.email || currentUser?.email || '';
        const book = books.find(b => b.id === item.bookId);
        const launchDate = book?.pubDate || book?.goLiveDate || book?.releaseDate || '';

        const createPreOrderFn = httpsCallable(functions, 'createPreOrder');
        const result = await createPreOrderFn({
          authorId: item.authorId || authorId,
          bookId: item.bookId || '',
          bookTitle: showCheckout.bookTitle || item.title || '',
          productType: type,
          productId: item.id,
          productTitle: item.title || '',
          price: type === 'ebook' && item.preOrder?.preOrderPrice ? item.preOrder.preOrderPrice : item.price,
          deliveryMethod: deliveryPref,
          deviceEmail: deliveryPref === 'device' ? deviceEmail : '',
          guestName,
          guestEmail,
          launchDate: launchDate ? new Date(launchDate instanceof Object ? launchDate.seconds * 1000 : launchDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '1 April 2026',
        });

        if (result.data?.success) {
          setCheckoutStep('confirmed');
        } else {
          throw new Error('Failed to create pre-order');
        }
      } else {
        // Normal purchases go through Stripe checkout
        const promoFields = storePromoApplied ? { promoCodeId: storePromoApplied.id, promoCode: storePromoApplied.code, freeShipping: !!storePromoApplied.freeShipping } : {};
        let fnName, payload;
        if (type === 'ebook') {
          fnName = 'createEbookCheckout';
          payload = { ebookId: item.id, authorId: item.authorId, ...promoFields };
        } else if (type === 'audiobook') {
          fnName = 'createAudiobookCheckout';
          payload = { audiobookId: item.id, authorId: item.authorId, ...promoFields };
        } else {
          fnName = 'createOrderCheckout';
          payload = {
            productId: item.id,
            bookId: item.bookId,
            authorId,
            shippingAddress: checkoutForm.shippingAddress || currentUser?.shippingAddress || {},
            readerName: checkoutForm.name || currentUser?.displayName || '',
            ...promoFields,
          };
        }

        const createCheckout = httpsCallable(functions, fnName);
        const result = await createCheckout(payload);

        if (result.data?.sessionUrl) {
          window.location.href = result.data.sessionUrl;
        } else {
          throw new Error('No checkout URL returned');
        }
      }
    } catch (err) {
      setCheckoutError(err.message);
      setCheckoutStep('form');
      setCheckoutLoading(false);
    }
  }

  // Poll for email verification
  useEffect(() => {
    if (checkoutStep !== 'verify' || !currentUser) return;
    const interval = setInterval(async () => {
      try {
        await currentUser.reload();
        if (currentUser.emailVerified) {
          clearInterval(interval);
          await proceedToStripe();
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStep, currentUser]);

  const displayName = branding?.displayName || author?.penName || author?.name || 'Author';

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
      </div>
    );
  }

  if (!author) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <h2 className="text-lg font-bold text-slate-900">Author not found</h2>
          <Button variant="outline" className="mt-4" onClick={() => { window.location.href = '/store'; }}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to store
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/30">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white/90 backdrop-blur-sm">
        <div className="mx-auto max-w-5xl px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/store'; }}
              className="gap-1.5 text-slate-600 hover:text-teal-700">
              <ArrowLeft className="h-4 w-4" /> All Authors
            </Button>
            <Button variant="outline" size="sm" onClick={() => { window.location.href = '/reader?signup=true'; }}
              className="gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50">
              Reader Portal
            </Button>
          </div>
        </div>
      </header>

      {/* Author banner */}
      <div className="relative">
        <div className="h-40 bg-gradient-to-br from-slate-200 to-teal-100">
          {branding?.banner && (
            <img src={branding.banner} alt="" className="h-full w-full object-cover" />
          )}
        </div>
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="relative -mt-12 flex items-end gap-5 pb-6">
            {branding?.logo ? (
              <img src={branding.logo} alt={displayName} className="h-24 w-24 rounded-2xl border-4 border-white object-cover shadow-md" />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded-2xl border-4 border-white bg-gradient-to-br from-slate-500 to-teal-600 text-3xl font-bold text-white shadow-md">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="pb-1">
              <h1 className="text-2xl font-bold text-slate-900">{displayName}</h1>
              {branding?.bio && <p className="mt-1 text-sm text-slate-500 line-clamp-2">{branding.bio}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="mb-8 rounded-xl border border-teal-200 bg-teal-50/50 px-5 py-3 text-sm text-slate-700">
          Want early access to books and to join {displayName}'s ARC team?{' '}
          <a href="/reader?signup=true" className="font-semibold text-teal-700 hover:underline">Register on the reader portal</a>
        </div>
      </div>

      {/* Books */}
      <main className="mx-auto max-w-5xl px-4 pb-16 sm:px-6">
        {books.length === 0 ? (
          <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center">
            <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-slate-500">No books available yet.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {books.map(book => {
              const bp = bookProducts[book.id] || { ebooks: [], audiobooks: [], products: [] };
              const statusBadge = getBookStatusLabel(book);
              const hasItems = bp.ebooks.length > 0 || bp.audiobooks.length > 0 || bp.products.length > 0;

              return (
                <Card key={book.id} className="overflow-hidden border-slate-200">
                  <div className="flex flex-col sm:flex-row">
                    {/* Book cover */}
                    <div className="flex shrink-0 items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-6 sm:w-48">
                      {book.coverUrl ? (
                        <img src={book.coverUrl} alt={book.title} className="h-48 rounded-lg object-cover shadow-md sm:h-56" />
                      ) : (
                        <div className="flex h-48 w-32 items-center justify-center rounded-lg bg-slate-300 text-5xl sm:h-56">
                          📖
                        </div>
                      )}
                    </div>

                    {/* Book details + products */}
                    <CardContent className="flex-1 p-6">
                      <div className="mb-4">
                        <div className="flex items-center gap-2">
                          <h2 className="text-xl font-bold text-slate-900">{book.title}</h2>
                          <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                        </div>
                        {book.series && <p className="text-sm font-medium text-teal-700">{book.series}</p>}
                        {book.blurb && <p className="mt-2 text-sm text-slate-600 line-clamp-3">{book.blurb}</p>}
                      </div>

                      {!hasItems ? (
                        <p className="text-sm text-slate-400">No products available yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {/* eBooks */}
                          {bp.ebooks.filter(eb => isGuestVisible(eb, 'ebook') && guestSettings.ebooks !== false).map(ebook => {
                            const purchasable = isGuestPurchasable(ebook, 'ebook');
                            const isFree = !ebook.price || ebook.price === 0;
                            const isPreOrder = ebook.preOrder?.preOrderEnabled || ebook.status === 'scheduled' || book.status === 'pre_launch_active';
                            const price = isPreOrder && ebook.preOrder?.preOrderPrice ? ebook.preOrder.preOrderPrice : ebook.price;

                            return (
                              <div key={ebook.id} className={`flex items-center justify-between rounded-xl border p-3 ${purchasable ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-70'}`}>
                                <div className="flex items-center gap-3">
                                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50">
                                    <BookText className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900">eBook{ebook.title ? ` — ${ebook.title}` : ''}</p>
                                    <p className="text-xs text-slate-500">{isFree ? 'Free' : `£${Number(price || 0).toFixed(2)}`}{isPreOrder ? ' (Pre-order)' : ''}</p>
                                  </div>
                                </div>
                                {purchasable ? (
                                  <Button size="sm" onClick={() => {
                                    setShowCheckout({ type: 'ebook', item: ebook, bookTitle: book.title, isPreOrder });
                                    setCheckoutStep(isPreOrder ? 'delivery' : 'form');
                                    setCheckoutError('');
                                    setDeliveryPref('online');
                                    setDeviceEmail('');
                                  }} className={isPreOrder ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-teal-600 text-white hover:bg-teal-700'}>
                                    <ShoppingCart className="mr-1 h-3.5 w-3.5" /> {isPreOrder ? 'Pre-Order' : 'Buy'}
                                  </Button>
                                ) : (
                                  <span className="flex items-center gap-1 text-xs text-slate-400">
                                    <Lock className="h-3 w-3" /> Register to unlock
                                  </span>
                                )}
                              </div>
                            );
                          })}

                          {/* Audiobooks */}
                          {bp.audiobooks.filter(ab => isGuestVisible(ab, 'audiobook') && guestSettings.audiobooks !== false).map(audiobook => {
                            const purchasable = isGuestPurchasable(audiobook, 'audiobook');
                            const isFree = !audiobook.price || audiobook.price === 0;
                            const isPreOrder = audiobook.preOrder?.preOrderEnabled || audiobook.status === 'scheduled' || book.status === 'pre_launch_active';
                            const price = isPreOrder && audiobook.preOrder?.preOrderPrice ? audiobook.preOrder.preOrderPrice : audiobook.price;

                            return (
                              <div key={audiobook.id} className={`flex items-center justify-between rounded-xl border p-3 ${purchasable ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-70'}`}>
                                <div className="flex items-center gap-3">
                                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-50">
                                    <Headphones className="h-4 w-4 text-purple-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900">Audiobook{audiobook.title ? ` — ${audiobook.title}` : ''}</p>
                                    <p className="text-xs text-slate-500">{isFree ? 'Free' : `£${Number(price || 0).toFixed(2)}`}{isPreOrder ? ' (Pre-order)' : ''}</p>
                                  </div>
                                </div>
                                {purchasable ? (
                                  <Button size="sm" onClick={() => {
                                    setShowCheckout({ type: 'audiobook', item: audiobook, bookTitle: book.title, isPreOrder });
                                    setCheckoutStep('form');
                                    setCheckoutError('');
                                  }} className={isPreOrder ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-teal-600 text-white hover:bg-teal-700'}>
                                    <ShoppingCart className="mr-1 h-3.5 w-3.5" /> {isPreOrder ? 'Pre-Order' : 'Buy'}
                                  </Button>
                                ) : (
                                  <span className="flex items-center gap-1 text-xs text-slate-400">
                                    <Lock className="h-3 w-3" /> Register to unlock
                                  </span>
                                )}
                              </div>
                            );
                          })}

                          {/* Physical products */}
                          {bp.products.filter(p => guestSettings.physical !== false).map(product => {
                            const isSigned = SIGNED_TYPES.includes(product.productType);
                            const amazonQuery = encodeURIComponent(book.title || product.name);

                            if (isSigned) {
                              return (
                                <div key={product.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 p-3 opacity-60">
                                  <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
                                      <Package className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-slate-500">{product.name}</p>
                                      <p className="text-xs text-slate-400">£{Number(product.price || 0).toFixed(2)}</p>
                                    </div>
                                  </div>
                                  <span className="flex items-center gap-1 text-xs text-slate-400">
                                    <Lock className="h-3 w-3" /> Register for the reader portal to purchase
                                  </span>
                                </div>
                              );
                            }

                            return (
                              <div key={product.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
                                    <Package className="h-4 w-4 text-slate-600" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-slate-900">{product.name}</p>
                                    {product.description && <p className="text-xs text-slate-500">{product.description}</p>}
                                  </div>
                                </div>
                                <a
                                  href={`https://www.amazon.co.uk/s?k=${amazonQuery}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900"
                                >
                                  <ExternalLink className="h-3 w-3" /> Amazon
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Checkout Dialog */}
      <Dialog open={!!showCheckout} onOpenChange={open => { if (!open) { setShowCheckout(null); setCheckoutStep('form'); setCheckoutError(''); setDeliveryPref('online'); setDeviceEmail(''); setStorePromoCode(''); setStorePromoApplied(null); setStorePromoError(''); } }}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900">
              {checkoutStep === 'confirmed' ? 'Order Complete' : checkoutStep === 'verify' ? 'Verify your email' : checkoutStep === 'processing' ? 'Redirecting...' : checkoutStep === 'delivery' ? 'How would you like to receive it?' : showCheckout?.isPreOrder ? 'Pre-Order Checkout' : 'Guest Checkout'}
            </DialogTitle>
          </DialogHeader>

          {checkoutStep === 'confirmed' ? (
            <div className="space-y-4 text-center py-4">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
                <ShoppingCart className="h-7 w-7 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Pre-order confirmed!</h3>
              <p className="text-sm text-slate-600">
                We've sent a confirmation email to <strong>{checkoutForm.email || currentUser?.email}</strong> with your order details.
              </p>
              {deliveryPref === 'device' && deviceEmail && (
                <>
                  <p className="text-sm text-slate-500">
                    Your eBook will be sent to <strong>{deviceEmail}</strong> when the book launches.
                  </p>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-left text-xs text-red-700">
                    <p className="font-semibold mb-1">Important: Add arc@narratoria.com to your Amazon approved senders</p>
                    <p>Without this step, Amazon will block the file. Go to your Amazon account → Personal Document Settings → Approved E-mail List → add <strong>arc@narratoria.com</strong></p>
                  </div>
                </>
              )}
              {deliveryPref !== 'device' && (
                <p className="text-xs text-slate-400">
                  Please add <strong>arc@narratoria.com</strong> to your contacts so you receive launch notifications.
                </p>
              )}
              <Button onClick={() => { setShowCheckout(null); setCheckoutStep('form'); }} className="w-full bg-teal-600 text-white hover:bg-teal-700">
                Done
              </Button>
            </div>

          ) : checkoutStep === 'processing' ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
              <p className="text-sm text-slate-500">Redirecting to secure checkout...</p>
            </div>

          ) : checkoutStep === 'verify' ? (
            <div className="space-y-4">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-teal-100">
                <Mail className="h-7 w-7 text-teal-600" />
              </div>
              <p className="text-center text-sm text-slate-600">
                We've sent a verification link to <strong>{currentUser?.email}</strong>. Click the link, then continue below.
              </p>
              <p className="text-center text-xs text-slate-400">This page will auto-detect verification.</p>

              {checkoutError && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">{checkoutError}</div>}

              <Button onClick={handleVerifyAndPay} disabled={checkoutLoading} className="w-full gap-2 bg-teal-600 text-white hover:bg-teal-700">
                {checkoutLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking...</> : <><RefreshCw className="h-4 w-4" /> I've verified — continue to payment</>}
              </Button>
              <button
                onClick={async () => {
                  setResendStatus('sending');
                  try { await sendEmailVerification(currentUser); setResendStatus('sent'); } catch { setResendStatus('error'); }
                }}
                disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                className="w-full text-center text-sm text-teal-600 hover:text-teal-700 disabled:text-slate-400"
              >
                {resendStatus === 'sending' ? 'Sending...' : resendStatus === 'sent' ? 'Sent!' : resendStatus === 'error' ? 'Failed — try again' : 'Resend verification email'}
              </button>
            </div>

          ) : checkoutStep === 'delivery' ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Choose how you'd like to receive your eBook when it launches:</p>

              <button
                onClick={() => setDeliveryPref('online')}
                className={`w-full rounded-xl border-2 p-4 text-left transition-all ${deliveryPref === 'online' ? 'border-teal-400 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <p className="font-semibold text-slate-900">Read online</p>
                <p className="text-sm text-slate-500">Access via the Narratoria reader in your browser when it goes live</p>
              </button>

              <button
                onClick={() => setDeliveryPref('device')}
                className={`w-full rounded-xl border-2 p-4 text-left transition-all ${deliveryPref === 'device' ? 'border-teal-400 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <p className="font-semibold text-slate-900">Send to device</p>
                <p className="text-sm text-slate-500">We'll send it to your Kindle or Kobo when the book launches</p>
              </button>

              {deliveryPref === 'device' && (
                <div>
                  <Label className="mb-1 block text-slate-600">Device email address *</Label>
                  <Input
                    type="email"
                    value={deviceEmail}
                    onChange={e => setDeviceEmail(e.target.value)}
                    placeholder="yourname@kindle.com or yourname@kobo.com"
                    required
                  />
                  <p className="mt-1 text-xs text-slate-400">Find this in your Kindle or Kobo account settings</p>
                </div>
              )}

              {checkoutError && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">{checkoutError}</div>}

              <Button
                onClick={() => {
                  if (deliveryPref === 'device' && !deviceEmail) {
                    setCheckoutError('Please enter your device email address.');
                    return;
                  }
                  setCheckoutError('');
                  if (currentUser) {
                    proceedToStripe();
                  } else {
                    setCheckoutStep('form');
                  }
                }}
                className="w-full gap-2 bg-teal-600 text-white hover:bg-teal-700"
              >
                <ShoppingCart className="h-4 w-4" /> {currentUser ? 'Continue to payment' : 'Continue'}
              </Button>
            </div>

          ) : (
            <div>
              {showCheckout && (
                <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">{showCheckout.bookTitle}</p>
                  <p className="text-xs text-slate-500">
                    {showCheckout.type === 'ebook' ? 'eBook' : showCheckout.type === 'audiobook' ? 'Audiobook' : 'Physical'}
                    {showCheckout.isPreOrder ? ' (Pre-order)' : ''} — £{Number(showCheckout.item.price || 0).toFixed(2)}
                  </p>
                </div>
              )}

              {/* Promo code input */}
              {showCheckout && !showCheckout.isPreOrder && (
                <div className="mb-4">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-teal-600 shrink-0" />
                    {storePromoApplied ? (
                      <div className="flex flex-1 items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-bold text-teal-700">{storePromoApplied.code}</code>
                          <span className="text-xs text-teal-600">
                            {storePromoApplied.freeShipping || storePromoApplied.discountType === 'free_shipping'
                              ? '🎉 Free shipping applied!'
                              : storePromoApplied.discountType === 'fixed'
                                ? `£${Number(storePromoApplied.discountValue).toFixed(2)} off`
                                : `${storePromoApplied.discountValue}% off`}
                          </span>
                        </div>
                        <button onClick={() => { setStorePromoApplied(null); setStorePromoCode(''); setStorePromoError(''); }} className="text-slate-400 hover:text-slate-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-1 gap-2">
                        <Input
                          value={storePromoCode}
                          onChange={e => { setStorePromoCode(e.target.value.toUpperCase()); setStorePromoError(''); }}
                          placeholder="Promo code"
                          className="font-mono text-sm"
                        />
                        <Button size="sm" variant="secondary"
                          onClick={validateStorePromo}
                          disabled={!storePromoCode.trim() || storePromoValidating}>
                          {storePromoValidating ? 'Checking...' : 'Apply'}
                        </Button>
                      </div>
                    )}
                  </div>
                  {storePromoError && <p className="mt-1 ml-6 text-xs text-red-500">{storePromoError}</p>}
                </div>
              )}

              {checkoutError && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">{checkoutError}</div>}

              {currentUser ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                    <User className="mr-1 inline h-4 w-4" /> Signed in as {currentUser.email}
                  </div>
                  {deliveryPref === 'device' && deviceEmail && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                      Delivery: Send to {deviceEmail}
                    </div>
                  )}
                  <Button onClick={() => proceedToStripe()} disabled={checkoutLoading} className="w-full gap-2 bg-teal-600 text-white hover:bg-teal-700">
                    {checkoutLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</> : <><ShoppingCart className="h-4 w-4" /> {showCheckout?.isPreOrder ? 'Place pre-order' : 'Continue to payment'}</>}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleGuestCheckout} className="space-y-3">
                  <div>
                    <Label className="mb-1 block text-slate-600">Name *</Label>
                    <Input value={checkoutForm.name} onChange={e => setCheckoutForm(f => ({ ...f, name: e.target.value }))} required placeholder="Your full name" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-slate-600">Email *</Label>
                    <Input type="email" value={checkoutForm.email} onChange={e => setCheckoutForm(f => ({ ...f, email: e.target.value }))} required placeholder="your@email.com" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-slate-600">Password *</Label>
                    <Input type="password" value={checkoutForm.password} onChange={e => setCheckoutForm(f => ({ ...f, password: e.target.value }))} required placeholder="Min 6 characters" minLength={6} />
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                    <h4 className="text-xs font-semibold text-slate-600">Shipping Address</h4>
                    <Input value={checkoutForm.shippingAddress.line1} onChange={e => setCheckoutForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, line1: e.target.value } }))} placeholder="Address Line 1" />
                    <Input value={checkoutForm.shippingAddress.line2} onChange={e => setCheckoutForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, line2: e.target.value } }))} placeholder="Address Line 2" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={checkoutForm.shippingAddress.city} onChange={e => setCheckoutForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, city: e.target.value } }))} placeholder="City" />
                      <Input value={checkoutForm.shippingAddress.postalCode} onChange={e => setCheckoutForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, postalCode: e.target.value } }))} placeholder="Postcode" />
                    </div>
                    <Input value={checkoutForm.shippingAddress.country} onChange={e => setCheckoutForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, country: e.target.value } }))} placeholder="Country" />
                  </div>

                  <Button type="submit" disabled={checkoutLoading} className="w-full gap-2 bg-teal-600 text-white hover:bg-teal-700">
                    {checkoutLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating account...</> : <><ShoppingCart className="h-4 w-4" /> {showCheckout?.isPreOrder ? 'Place pre-order' : 'Continue to payment'}</>}
                  </Button>
                  <p className="text-center text-xs text-slate-400">Email verification required before payment</p>
                </form>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-slate-100 bg-white py-6">
        <div className="mx-auto max-w-5xl px-4 text-center text-xs text-slate-400 sm:px-6">
          <p>Narratoria Ltd</p>
        </div>
      </footer>
    </div>
  );
}
